import { notifications } from '@mantine/notifications';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { RootState } from '@/store';
import { API_ENDPOINTS, API_SLICE, BASE_URL } from '@/utils/constants/apiEndpoints.ts';

import { FileUploadRequest, FileUploadResponse, GetFileResponse } from '@/types/file';

export const filesApiSlice = createApi({
  reducerPath: 'filesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  tagTypes: [API_SLICE.File],
  endpoints: (builder) => ({
    uploadFile: builder.mutation<FileUploadResponse, FileUploadRequest>({
      query: (params) => {
        const formData = new FormData();

        if (params.file) {
          formData.append('file', params.file);
          formData.append('path', params.path);
        } else {
          console.error('File is missing or invalid:', params.file);
        }

        return {
          url: API_ENDPOINTS.FILES.UPLOAD,
          method: 'POST',
          body: formData
        };
      }
    }),
    getFiles: builder.query<GetFileResponse[], string[]>({
      query: (fileIds) => ({
        url: API_ENDPOINTS.FILES.ROOT,
        method: 'GET',
        params: { fileIds }
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error: any) {
          // Check for 413 status code (URL too long with many fileIds)
          if (error?.error?.status === 413) {
            notifications.show({
              title: 'Request Failed',
              message:
                'Requested Images are too large. Please try with fewer file IDs. or Contact support.',
              color: 'red',
              autoClose: 5000
            });
          } else {
            // Handle other errors
            notifications.show({
              title: 'File Retrieval Error',
              message: error?.error?.data?.message || 'Failed to retrieve files. Please try again.',
              color: 'red',
              autoClose: 5000
            });
          }
          console.error('GetFiles error:', error);
        }
      }
    })
  })
});

export const { useUploadFileMutation, useGetFilesQuery } = filesApiSlice;
